<template>
  <aside :class="ns.b()">
    <div :class="ns.e('avatar')">
      <z-tooltip
        v-if="$slots['user-menu']"
        :disabled="!displayName || menuVisible"
        :content="displayName"
        placement="right"
      >
        <span :class="ns.e('avatar-trigger')">
          <z-popover
            v-model:visible="menuVisible"
            placement="right-start"
            :width="200"
            trigger="click"
            effect="light"
            :show-arrow="false"
            :offset="10"
            :popper-class="ns.e('user-popper')"
          >
            <template #reference>
              <button
                type="button"
                :class="ns.e('avatar-btn')"
                aria-label="用户菜单"
              >
                <img
                  v-if="avatar && !avatarFailed"
                  :class="ns.e('avatar-img')"
                  :src="avatar"
                  alt=""
                  @error="avatarFailed = true"
                />
                <span v-else :class="ns.e('avatar-fallback')">
                  {{ fallbackLetter }}
                </span>
              </button>
            </template>
            <slot name="user-menu" :close="closeMenu" />
          </z-popover>
        </span>
      </z-tooltip>
      <z-tooltip v-else :disabled="!displayName" :content="displayName" placement="right">
        <button
          type="button"
          :class="ns.e('avatar-btn')"
          aria-label="用户"
          @click="emit('avatar-click')"
        >
          <img
            v-if="avatar && !avatarFailed"
            :class="ns.e('avatar-img')"
            :src="avatar"
            alt=""
            @error="avatarFailed = true"
          />
          <span v-else :class="ns.e('avatar-fallback')">
            {{ fallbackLetter }}
          </span>
        </button>
      </z-tooltip>
    </div>

    <div :class="ns.e('tabs')">
      <slot />
      <template v-for="item in items" :key="item.key">
        <z-tooltip
          :disabled="!item.title"
          :content="item.title"
          placement="right"
        >
          <span :class="ns.e('entry')">
            <z-badge
              :value="item.badge"
              :hidden="!item.badge"
              :max="99"
            >
              <button
                type="button"
                :class="[ns.e('tab'), ns.is('active', !!item.active)]"
                :aria-label="item.title"
                :disabled="item.disabled"
                @click="emit('select', item.key)"
              >
                <z-icon v-if="item.icon">
                  <component :is="resolveIcon(item.icon)" />
                </z-icon>
              </button>
            </z-badge>
          </span>
        </z-tooltip>
      </template>
    </div>

    <div :class="ns.e('bottom')">
      <template v-for="item in bottomItems" :key="item.key">
        <z-tooltip
          :disabled="!item.title"
          :content="item.title"
          placement="right"
        >
          <span :class="ns.e('entry')">
            <z-badge
              :value="item.badge"
              :hidden="!item.badge"
              :max="99"
            >
              <button
                type="button"
                :class="[ns.e('tab'), ns.is('active', !!item.active)]"
                :aria-label="item.title"
                :disabled="item.disabled"
                @click="emit('select', item.key)"
              >
                <z-icon v-if="item.icon">
                  <component :is="resolveIcon(item.icon)" />
                </z-icon>
              </button>
            </z-badge>
          </span>
        </z-tooltip>
      </template>
      <slot name="bottom" />
    </div>
  </aside>
</template>

<script lang="ts" setup>
import { computed, ref, watch } from 'vue'
import { useNamespace } from '@zoryn/hooks'
import ZBadge from '@zoryn/components/badge'
import ZIcon from '@zoryn/components/icon'
import ZPopover from '@zoryn/components/popover'
import ZTooltip from '@zoryn/components/tooltip'
import { resolveIcon } from './resolve-icon'
import type { AppRailItem } from './types'

defineOptions({
  name: 'ZAppRail',
})

const props = withDefaults(
  defineProps<{
    avatar?: string
    displayName?: string
    items?: AppRailItem[]
    bottomItems?: AppRailItem[]
  }>(),
  {
    avatar: '',
    displayName: '',
    items: () => [],
    bottomItems: () => [],
  }
)

const emit = defineEmits<{
  select: [key: string]
  'avatar-click': []
}>()

const ns = useNamespace('app-rail')
const menuVisible = ref(false)
const avatarFailed = ref(false)

const fallbackLetter = computed(() =>
  (props.displayName || '?').slice(0, 1).toUpperCase()
)

watch(
  () => props.avatar,
  () => {
    avatarFailed.value = false
  }
)

function closeMenu() {
  menuVisible.value = false
}
</script>
